import type { ProcessWorkflow } from '../../containers/process/context/process.context.type';
import type { TaskTypeValue } from '../../containers/tasks-table/tasks-table.type';
import type { ResolvedPhaseLabels } from '../../helpers/phase-labels/phase-labels.type';

enum GraphType {
  Common = 'common',
  Super = 'super',
}

type GraphNode = {
  name: string;
  order: number;
};

type GraphEdge = Record<string, number>;

type GraphPath = {
  [key: string]: number;
};

type GraphMetrics = {
  backwards_tasks: number;
  skipped_tasks: number;
  blocked_tasks: number;
};
type GraphData = {
  task_count: number;
  nodes: GraphNode[];
  edges: Record<string, GraphEdge>;
  common_paths: GraphPath[];
  cycle_times?: Record<string, number>;
  costs?: Record<string, number>;
  metrics?: GraphMetrics;
  task_types?: string[];
  currency_code: string;
};

enum Stages {
  Blocked = 'blocked',
  Backlog = 'backlog',
  InProgress = 'wip',
  InReview = 'review',
  InTest = 'test',
  Deployable = 'deployable',
  Done = 'done',
}

enum WorkflowStatus {
  Expected = 'Expected',
  Skipped = 'Skipped',
  Backwards = 'Backwards',
  Completed = 'Completed',
}

type ProcessGraphConfig = Partial<{
  teamId: string;
  workflowId: string;
  boardId: string;
  workPeriodId: string;
  taskTypes: TaskTypeValue[] | null;
  epicId: string;
  taskId: string;
  graphType: GraphType;
  startDate: string;
  endDate: string;
  useBloomfilterSections: boolean;
}>;

type ProcessTasksConfig = {
  teamId: string | null;
  workflowId: string | null;
  boardId: string | null;
  workPeriodId: string | null;
  taskTypes: TaskTypeValue[] | null;
  epicId: string | null;
  taskId?: string | null;
  startDate?: string;
  endDate?: string;
  processWorkflow?: ProcessWorkflow;
  useGroups?: boolean;
};

type ProcessTask = {
  id: string;
  title: string;
  name: string;
  description: string;
  points: number;
  type: string;
  raw_type: string | null;
  raw_type_label: string | null;
  created_date: string;
  url: string;
  project_id: string;
  epic_key: string;
  epic: {
    id: string;
    name: string;
  } | null;
  initiatives: {
    id: string;
    name: string;
  }[];
  transitions?: {
    status: string;
    changed_by: string;
    changed_at: string;
  }[];
};

type ProcessTasksData = {
  tasks: ProcessTask[];
  task_count: number;
  arrived: Record<string, string[]>;
  departed: Record<string, string[]>;
  returned: Record<string, string[]>;
};

type ProcessMapping = {
  bf_happy_path: string[];
  customer_to_bf: Record<string, string | null>;
  bf_to_customer: Record<string, string[]>;
  /**
   * Per-workflow display labels for canonical phase keys, emitted by the
   * backend on workflow-scoped `external_section_map` responses.
   * Optional because project-scoped responses omit it; consumers should
   * route through `resolvePhaseLabels` for the i18n fallback.
   */
  labels?: ResolvedPhaseLabels;
};

/**
 * A labeled work-item type option from the process task-types endpoint.
 * `value` is the exact stored `raw_type` (the key fed back into the graph's
 * `task_types` param); `label` is the verbatim external casing
 * (`raw_type_label`), equal to `value` when no label has been synced yet.
 */
type ProcessTaskTypeOption = {
  value: string;
  label: string;
};

/**
 * Combined result of the process task-types endpoint: the legacy list of
 * raw type strings plus the labeled options introduced alongside it. Both
 * come from a single HTTP response.
 */
type ProcessTaskTypesData = {
  taskTypes: string[];
  taskTypeOptions: ProcessTaskTypeOption[];
};

export { GraphType, Stages, WorkflowStatus };
export type {
  GraphData,
  GraphNode,
  ProcessGraphConfig,
  ProcessMapping,
  ProcessTask,
  ProcessTasksConfig,
  ProcessTasksData,
  ProcessTaskTypeOption,
  ProcessTaskTypesData,
};
